import React from "react";
import Helmet from "react-helmet";

interface PlausibleScriptProps {
  performanceCookieConsent: boolean;
  language: string;
  pathname: string;
}

/**
 * Strips the locale prefix from a pathname (e.g., /es-mx/schools → /schools).
 * Locale prefixes are 2-3 letter language code + hyphen + 2 letter country code (e.g., es-mx, fil-ph).
 */
function getBasePath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && /^[a-z]{2,3}-[a-z]{2}$/i.test(segments[0])) {
    const rest = segments.slice(1).join("/");
    return rest ? "/" + rest : "/";
  }
  return pathname || "/";
}

const LANDING_PREFIXES = ["/families", "/schools", "/teachers", "/districts"];
const PRODUCT_PREFIXES = ["/dojo-islands", "/dojo-tutor", "/dojo-sparks", "/plus"];
const BRANDS = ["dojo-islands", "dojo-tutor", "dojo-sparks", "plus"];

function pathStartsWith(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(prefix + "/");
}

function getPageType(pathname: string): string | undefined {
  const basePath = getBasePath(pathname);
  if (basePath === "/") return "landing";
  if (LANDING_PREFIXES.some((p) => pathStartsWith(basePath, p))) return "landing";
  if (PRODUCT_PREFIXES.some((p) => pathStartsWith(basePath, p))) return "product";
  return undefined;
}

function getBrand(pathname: string): string | undefined {
  const basePath = getBasePath(pathname);
  for (const brand of BRANDS) {
    if (pathStartsWith(basePath, "/" + brand)) return brand;
  }
  return undefined;
}

const PLAUSIBLE_SCRIPT_SRC =
  "https://plausible.classdojo.com/js/script.file-downloads.hash.outbound-links.pageview-props.revenue.tagged-events.js";

const PlausibleScript: React.FC<PlausibleScriptProps> = ({ performanceCookieConsent, language, pathname }) => {
  const isProd = process.env.GATSBY_ENV === "production";

  if (!isProd || !performanceCookieConsent) return null;

  const pageType = getPageType(pathname);
  const brand = getBrand(pathname);

  // Build script props dynamically so we only include event-* attributes that have values.
  // Helmet converts undefined attribute values to empty strings, so we must omit them entirely.
  const scriptProps: Record<string, string> = {
    defer: "true",
    "data-domain": "classdojo.com",
    "event-language": language,
    src: PLAUSIBLE_SCRIPT_SRC,
  };
  if (pageType) scriptProps["event-page_type"] = pageType;
  if (brand) scriptProps["event-brand"] = brand;

  return (
    <Helmet>
      <script {...scriptProps}></script>
      <script>{`
          window.plausible = window.plausible || function() {
            (window.plausible.q = window.plausible.q || []).push(arguments)
          }
      `}</script>
    </Helmet>
  );
};

export default PlausibleScript;
